import React from 'react';
import Button, {ButtonProps} from '@mui/material/Button';
import clsx from 'clsx';
import './ArrButton.scss';
import {CircularProgress} from '@mui/material';

export type ArrButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'text';

export type ArrButtonSize = 'large' | 'medium' | 'small' | 'xs';

export interface AurButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  variant?: ArrButtonVariant;
  size?: ArrButtonSize;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  progressCircleSize?: number;
  loading?: boolean;
  target?: '_self' | '_blank' | '_parent' | '_top';
  dataTestId: string;
  disabled?: boolean;
  endIconSize?: 'small' | 'medium' | 'large';
  isAriaDisabled?: boolean;
}

const ArrButton: React.FC<AurButtonProps> = ({
  children,
  variant = 'primary',
  size = 'large',
  startIcon,
  endIcon,
  progressCircleSize,
  loading = false,
  className,
  disabled = false,
  isAriaDisabled = false,
  dataTestId = 'arr-btn',
  endIconSize,
  ...props
}) => {
  const defaultProgressSize = size === 'medium' ? 20 : 18;

  const progressSize =
    progressCircleSize ?? (size === 'large' ? 24 : defaultProgressSize);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isAriaDisabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    props.onClick?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isAriaDisabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    props.onKeyDown?.(e);
  };

  return (
    <Button
      {...props}
      disableRipple
      className={clsx(
        'aur-button',
        `aur-button--${variant}`,
        `aur-button--${size}`,
        endIconSize && `aur-button--end-icon-${endIconSize}`,
        {
          'aur-button--aria-disabled': isAriaDisabled,
        },
        className
      )}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={isAriaDisabled ? false : disabled}
      aria-disabled={isAriaDisabled ? true : disabled}
      data-testid={dataTestId}
    >
      {loading && (
        <CircularProgress
          size={progressSize}
          color="inherit"
          className="progress-spinner"
        />
      )}
      {children}
    </Button>
  );
};

export default ArrButton;
